import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { S, Icon } from '../../screens/regureStyles';

// ─── ConfirmDialog ─────────────────────────────────────────
// Modern replacement for window.confirm — full-viewport backdrop +
// centered card that matches the app's design system. Portal-mounted
// to document.body so it renders above every screen regardless of the
// caller's overflow / stacking context.
//
// Usage:
//   const [confirm, setConfirm] = useState(null);
//   setConfirm({
//     title:       'Delete bordereau?',
//     message:     'This action writes an audit entry and cannot be undone.',
//     confirmText: 'Delete',
//     variant:     'danger',
//     onConfirm:   async () => {
//       await api.delete(...);
//       setConfirm(null);
//     },
//   });
//   {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
//
// Props:
//   title       — heading string (required)
//   message     — body copy (string or ReactNode)
//   confirmText — CTA button label; defaults to t('common.confirm', 'Confirm')
//   cancelText  — cancel button label; defaults to t('common.cancel', 'Cancel')
//   variant     — 'danger' | 'warning' | 'primary'; drives the icon + CTA colour
//   onConfirm   — async () => void; button shows a spinner while pending
//   onCancel    — () => void; also fired on Escape + backdrop click

const VARIANT_STYLES = {
  danger:  { icon: 'trash',   accent: '#dc2626', ctaBg: '#dc2626', ctaBgHover: '#b91c1c' },
  warning: { icon: 'alert-circle', accent: '#d97706', ctaBg: '#d97706', ctaBgHover: '#b45309' },
  primary: { icon: 'check',   accent: S.burgundy, ctaBg: S.burgundy, ctaBgHover: S.burgundy2 },
};

const ConfirmDialog = ({
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const cfg = VARIANT_STYLES[variant] || VARIANT_STYLES.danger;

  // Escape closes. Focus trap is skipped — this is a short-lived
  // confirmation, not a form; standard tab order suffices.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy && onCancel) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const handleConfirm = async () => {
    if (!onConfirm || busy) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  const node = (
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby='confirm-dialog-title'
      onClick={() => !busy && onCancel && onCancel()}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 20px 60px rgba(15,23,42,0.35)',
          width: '100%', maxWidth: 460,
          overflow: 'hidden',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Inter', sans-serif",
        }}
      >
        <div style={{ padding: '24px 24px 8px 24px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 12,
              background: `${cfg.accent}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name={cfg.icon} color={cfg.accent} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              id='confirm-dialog-title'
              style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}
            >
              {title}
            </div>
            {message && (
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                {message}
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex', justifyContent: 'flex-end', gap: 8,
            padding: '16px 24px 20px 24px',
          }}
        >
          <button
            type='button'
            onClick={() => !busy && onCancel && onCancel()}
            disabled={busy}
            style={{
              padding: '9px 16px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13, fontWeight: 600,
              color: '#475569',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {cancelText || t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type='button'
            onClick={handleConfirm}
            disabled={busy}
            autoFocus
            style={{
              padding: '9px 18px',
              background: cfg.ctaBg,
              border: 'none',
              borderRadius: 8,
              fontSize: 13, fontWeight: 700,
              color: '#fff',
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.75 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => { if (!busy) e.currentTarget.style.background = cfg.ctaBgHover; }}
            onMouseLeave={e => { if (!busy) e.currentTarget.style.background = cfg.ctaBg; }}
          >
            {busy && (
              <span
                style={{
                  width: 12, height: 12,
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'confirmSpin 0.7s linear infinite',
                  display: 'inline-block',
                }}
              />
            )}
            {confirmText || t('common.confirm', { defaultValue: 'Confirm' })}
          </button>
        </div>
        <style>{`@keyframes confirmSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  // Portal so we escape any parent overflow/stacking context.
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(node, document.body);
};

export default ConfirmDialog;
